import { useEffect, useState, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  CircularProgress,
  Alert,
  Paper,
  Grid,
  Tooltip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from '@mui/material';
import {
  Visibility as ViewIcon,
} from '@mui/icons-material';
import { adminApi } from '../../api';
import type { WebhookEventsResult, WebhookEvent, WebhookStats } from '../../api/admin';
import { isAdmin } from '../../types';
import { useAuth } from '../../hooks/useAuth';
import { useDocumentTitle, getStoredRowsPerPage, setStoredRowsPerPage } from '../../hooks/core';
import { AdminBreadcrumbs, WebhookStatusChip as StatusChip, WebhookStatsCards } from '../../components/admin';
import { formatDateTime } from '../../utils/dateFormatting';

const EVENT_TYPES = [
  'delivered',
  'opened',
  'clicked',
  'unsubscribed',
  'complained',
  'bounced',
  'dropped',
  'failed',
];

export function WebhookEventsPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user: currentUser } = useAuth();

  useDocumentTitle('Webhook Events');

  const [result, setResult] = useState<WebhookEventsResult | null>(null);
  const [stats, setStats] = useState<WebhookStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Filters
  const [providerFilter, setProviderFilter] = useState<string>(
    searchParams.get('provider') || ''
  );
  const [eventTypeFilter, setEventTypeFilter] = useState<string>(
    searchParams.get('eventType') || ''
  );
  const [statusFilter, setStatusFilter] = useState<string>(
    searchParams.get('status') || ''
  );
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(() => getStoredRowsPerPage());

  // Detail dialog
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState<WebhookEvent | null>(null);

  const fetchEvents = useCallback(async () => {
    try {
      setLoading(true);
      const [eventsData, statsData] = await Promise.all([
        adminApi.getWebhookEvents({
          provider: providerFilter || undefined,
          eventType: eventTypeFilter || undefined,
          status: statusFilter || undefined,
          page: page + 1,
          pageSize,
        }),
        adminApi.getWebhookStats(),
      ]);
      setResult(eventsData);
      setStats(statsData);
    } catch (err) {
      setError('Failed to load webhook events');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [providerFilter, eventTypeFilter, statusFilter, page, pageSize]);

  useEffect(() => {
    if (!isAdmin(currentUser)) {
      navigate('/');
      return;
    }
    fetchEvents();
  }, [currentUser, navigate, fetchEvents]);

  const updateSearchParam = (key: string, value: string) => {
    const params = new URLSearchParams(searchParams);
    if (value) {
      params.set(key, value);
    } else {
      params.delete(key);
    }
    setSearchParams(params);
  };

  const handleProviderChange = (value: string) => {
    setProviderFilter(value);
    setPage(0);
    updateSearchParam('provider', value);
  };

  const handleEventTypeChange = (value: string) => {
    setEventTypeFilter(value);
    setPage(0);
    updateSearchParam('eventType', value);
  };

  const handleStatusChange = (value: string) => {
    setStatusFilter(value);
    setPage(0);
    updateSearchParam('status', value);
  };

  const handleViewDetails = (event: WebhookEvent) => {
    setSelectedEvent(event);
    setDetailDialogOpen(true);
  };

  if (loading && !result) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <AdminBreadcrumbs items={[{ label: 'Email' }, { label: 'Webhook Events' }]} />
      <Typography variant="h4" sx={{ mb: 3 }}>
        Webhook Events
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Stats */}
      {stats && <WebhookStatsCards stats={stats} />}

      {/* Event Types Breakdown */}
      {stats && stats.eventsByType && Object.keys(stats.eventsByType).length > 0 && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2 }}>
              Events by Type
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {Object.entries(stats.eventsByType).map(([type, count]) => (
                <Chip
                  key={type}
                  label={`${type}: ${count}`}
                  variant="outlined"
                  size="small"
                />
              ))}
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <FormControl sx={{ minWidth: 150 }} size="small">
              <InputLabel>Provider</InputLabel>
              <Select
                value={providerFilter}
                label="Provider"
                onChange={(e) => handleProviderChange(e.target.value)}
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="mailgun">Mailgun</MenuItem>
              </Select>
            </FormControl>
            <FormControl sx={{ minWidth: 150 }} size="small">
              <InputLabel>Event Type</InputLabel>
              <Select
                value={eventTypeFilter}
                label="Event Type"
                onChange={(e) => handleEventTypeChange(e.target.value)}
              >
                <MenuItem value="">All</MenuItem>
                {EVENT_TYPES.map((type) => (
                  <MenuItem key={type} value={type}>
                    {type.charAt(0).toUpperCase() + type.slice(1)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl sx={{ minWidth: 150 }} size="small">
              <InputLabel>Status</InputLabel>
              <Select
                value={statusFilter}
                label="Status"
                onChange={(e) => handleStatusChange(e.target.value)}
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="pending">Pending</MenuItem>
                <MenuItem value="processed">Processed</MenuItem>
                <MenuItem value="failed">Failed</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </CardContent>
      </Card>

      {/* Events Table */}
      <Card>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Date</TableCell>
                <TableCell>Provider</TableCell>
                <TableCell>Event Type</TableCell>
                <TableCell>Event ID</TableCell>
                <TableCell align="center">Status</TableCell>
                <TableCell>Processed At</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {result?.events.map((event) => (
                <TableRow key={event.id} hover>
                  <TableCell>
                    <Typography variant="body2">
                      {formatDateTime(event.createdAt)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={event.provider}
                      size="small"
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight="medium">
                      {event.eventType}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography
                      variant="body2"
                      fontFamily="monospace"
                      sx={{
                        maxWidth: 200,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {event.eventId}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <StatusChip status={event.status} />
                    {event.errorMessage && (
                      <Tooltip title={event.errorMessage}>
                        <Typography
                          variant="caption"
                          color="error"
                          sx={{ display: 'block', mt: 0.5, cursor: 'help' }}
                        >
                          Error
                        </Typography>
                      </Tooltip>
                    )}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {event.processedAt ? formatDateTime(event.processedAt) : '—'}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip title="View Details">
                      <IconButton
                        size="small"
                        onClick={() => handleViewDetails(event)}
                      >
                        <ViewIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
              {result?.events.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">
                      No webhook events found
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        {result && (
          <TablePagination
            component="div"
            count={result.total}
            page={page}
            onPageChange={(_, newPage) => setPage(newPage)}
            rowsPerPage={pageSize}
            onRowsPerPageChange={(e) => {
              const newSize = parseInt(e.target.value, 10);
              setPageSize(newSize);
              setStoredRowsPerPage(newSize);
              setPage(0);
            }}
            rowsPerPageOptions={[10, 20, 50, 100]}
          />
        )}
      </Card>

      {/* Detail Dialog */}
      <Dialog
        open={detailDialogOpen}
        onClose={() => setDetailDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Webhook Event Details</DialogTitle>
        <DialogContent>
          {selectedEvent && (
            <Box sx={{ mt: 1 }}>
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Typography variant="caption" color="text.secondary">
                    Provider
                  </Typography>
                  <Typography variant="body1" sx={{ mb: 2 }}>
                    {selectedEvent.provider}
                  </Typography>
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Typography variant="caption" color="text.secondary">
                    Event Type
                  </Typography>
                  <Typography variant="body1" sx={{ mb: 2 }}>
                    {selectedEvent.eventType}
                  </Typography>
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Typography variant="caption" color="text.secondary">
                    Status
                  </Typography>
                  <Box sx={{ mb: 2 }}>
                    <StatusChip status={selectedEvent.status} />
                  </Box>
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Typography variant="caption" color="text.secondary">
                    Event ID
                  </Typography>
                  <Typography variant="body2" fontFamily="monospace" sx={{ mb: 2 }}>
                    {selectedEvent.eventId}
                  </Typography>
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Typography variant="caption" color="text.secondary">
                    Created
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 2 }}>
                    {formatDateTime(selectedEvent.createdAt)}
                  </Typography>
                </Grid>
                {selectedEvent.processedAt && (
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Typography variant="caption" color="text.secondary">
                      Processed
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 2 }}>
                      {formatDateTime(selectedEvent.processedAt)}
                    </Typography>
                  </Grid>
                )}
                {selectedEvent.emailLogId && (
                  <Grid size={12}>
                    <Typography variant="caption" color="text.secondary">
                      Related Email Log ID
                    </Typography>
                    <Typography variant="body2" fontFamily="monospace" sx={{ mb: 2 }}>
                      {selectedEvent.emailLogId}
                    </Typography>
                  </Grid>
                )}
                {selectedEvent.errorMessage && (
                  <Grid size={12}>
                    <Alert severity="error">
                      <Typography variant="body2">
                        {selectedEvent.errorMessage}
                      </Typography>
                    </Alert>
                  </Grid>
                )}
                <Grid size={12}>
                  <Typography variant="caption" color="text.secondary">
                    Payload
                  </Typography>
                  <Paper variant="outlined" sx={{ p: 2, mt: 1, bgcolor: 'grey.50' }}>
                    <pre style={{ margin: 0, overflow: 'auto', fontSize: '0.75rem', maxHeight: 300 }}>
                      {JSON.stringify(selectedEvent.payload, null, 2)}
                    </pre>
                  </Paper>
                </Grid>
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
